// Account Statement result modal for the Super Over family (superover,
// superover2, superover3, cricketv3). The original site shows these like a
// SPORTS market, not a casino round:
//   cricket -> superover2 -> IND Vs ENG Super Over2/R.No.:48235791342 -> Fancy1
//   Winner: Wicket                              Game Time: 23/09/2026 12:23:26
//   [All/Back/Lay/Deleted]  Nation | Rate | Bhav | Amount | Win | Date | Ip | Browser
// Everything here is derived from detail-results (t1.score = one row per ball
// with team `nat`, over `oc`, `run`, `wkt`) + the row's own bet — same reading
// of the score the settlement worker uses (resolveSuperover3 / helper/superOverSettle.js).

import { formatBetOdds } from "../../../utils/casinoBook.js";

export const SUPEROVER_STATEMENT_GAMES = new Set(["superover", "superover2", "superover3", "cricketv3"]);

export const isSuperOverStatementGame = (code) => SUPEROVER_STATEMENT_GAMES.has(String(code || "").toLowerCase());

const up = (v) => String(v ?? "").trim().toUpperCase();

// Feed names drift ("Australia" vs "AUS") — prefix match either way.
function teamMatches(a, b) {
  const x = up(a), y = up(b);
  if (!x || !y) return false;
  return x === y || x.startsWith(y) || y.startsWith(x);
}

// Bet table market titles (SuperOverMarkets.jsx): Bookmaker / Tie / Fancy1 / Fancy.
export function superOverMarketOf(selection) {
  const s = up(selection);
  if (/^0\.\d\s+OVER\s+/.test(s)) return "Fancy1";
  if (s === "TIE") return "Tie";
  if (s.includes("OVER") || s.includes("RUN") || s.includes("BHAV")) return "Fancy";
  return "Bookmaker";
}

const BALL_WORD = { 0: "Zero", 1: "One", 2: "Two", 3: "Three", 4: "Four", 6: "Six" };
const ballOutcome = (b) => (b.wkt ? "Wicket" : (BALL_WORD[Number(b.run)] ?? String(b.run)));

// "9/23/2026 12:23:26 PM" (vendor local time, no zone) -> "23/09/2026 12:23:26".
export function fmtVendorTime(s) {
  const m = String(s || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return String(s || "");
  let h = Number(m[4]);
  const ap = (m[7] || "").toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(m[2])}/${p(m[1])}/${m[3]} ${p(h)}:${m[5]}:${m[6]}`;
}

// Market-specific winner, the way the original labels it:
//   Bookmaker / Tie -> winnat ("IND")
//   Fancy1 ball     -> that ball's outcome ("Wicket", "Four", "Zero", …)
//   Fancy session   -> the team's innings runs ("12"), as settled
function superOverWinner(t1, selection) {
  const sel = up(selection);
  const score = Array.isArray(t1?.score) ? t1.score : [];
  const winnat = t1?.winnat ? String(t1.winnat) : "";

  const ball = sel.match(/^0\.(\d)\s+OVER\s+[A-Z]+\s+([A-Z]+)$/);
  if (ball) {
    const balls = score.filter((b) => b && teamMatches(b.nat, ball[2])).sort((a, b) => a.cid - b.cid);
    const bd = balls[Number(ball[1]) - 1];
    return bd ? ballOutcome(bd) : "";
  }

  // "1 OVER RUN [BHAV] IND 13" / "IND OVER 1 15"
  const runFancy = sel.match(/^(\d+)\s+OVER\s+RUN\s+(?:BHAV\s+)?([A-Z]+)/);
  const overSession = sel.match(/^([A-Z]+)\s+OVER\s+(\d+)\s+\d/);
  if (runFancy || overSession) {
    const team = runFancy ? runFancy[2] : overSession[1];
    const over = Number(runFancy ? runFancy[1] : overSession[2]);
    // 5Five "IND OVER 3 45" is the total at the END of over 3 (cumulative); a Super
    // Over session row is the whole innings — both as settled
    const balls = score.filter((b) => b && teamMatches(b.nat, team) && (!overSession || Number(b.oc) <= over));
    return balls.length ? String(balls.reduce((s, b) => s + (Number(b.run) || 0), 0)) : "";
  }

  return winnat;
}

/**
 * meta for SportsResultModal + the bets narrowed to the row's market.
 * @param detail   detail-results response (t1 under data.data or data)
 * @param header   statement casino-bets header ({ gameCode, roundId, gamename })
 * @param bets     casino-bets rows (raw endpoint rows)
 * @param betId    the statement row's bet (its market picks the breadcrumb/winner)
 */
export function buildSuperOverStatement({ detail, header, bets, betId }) {
  const t1 = detail?.data?.data?.t1 || detail?.data?.t1 || null;
  const code = String(header?.gameCode || "").toLowerCase();
  const rows = Array.isArray(bets) ? bets : [];
  const own = betId ? rows.find((b) => Number(b.id) === Number(betId)) : null;
  const marketBet = own || rows[0] || null;
  const marketType = marketBet ? superOverMarketOf(marketBet.selection ?? marketBet.player_name) : "";

  // Teams in innings order from the score; fall back to the bet's round.
  const teams = [];
  for (const b of Array.isArray(t1?.score) ? t1.score : []) {
    const n = String(b?.nat || "").trim();
    if (n && !teams.includes(n)) teams.push(n);
  }
  const label = header?.gamename || code;
  const matchTitle = teams.length >= 2 ? `${teams[0]} Vs ${teams[1]} ${label}` : label;
  const roundId = header?.roundId || marketBet?.round_id || "";

  const path = ["cricket", code, `${matchTitle}/R.No.:${roundId}`, marketType]
    .filter(Boolean).join(" -> ");

  const marketBets = marketType
    ? rows.filter((b) => superOverMarketOf(b.selection ?? b.player_name) === marketType)
    : rows;

  return {
    meta: {
      path,
      winner: marketBet ? superOverWinner(t1, marketBet.selection ?? marketBet.player_name) : (t1?.winnat || ""),
      gameTime: t1?.mtime ? fmtVendorTime(t1.mtime) : "",
    },
    // SportsResultModal row shape (nation/rate/amount/win/date/ip/browser/side/deleted).
    bets: marketBets.map((b) => ({
      ...b,
      nation: b.player_name || b.selection || "",
      rate: formatBetOdds(code, b, b.odds),
      bhav: 0,
      amount: b.stake,
      date: b.created_at,
      ipAddress: b.ip_address || "",
      casino: true,
    })),
  };
}
